import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Typography } from '@mui/material'

import { getUnlockPinAsync } from '../../api/unlockPinApi'
import { IRootState } from '../../redux/reducers'
import { AppError } from '../common/AppError'
import { ErrorMessage } from '../common/ErrorBox'
import SlideInPage from '../common/SlideInPage'
import Spinner from '../common/Spinner'
import { isScreenSizeSmall } from '../common/utils'

import { getErrorMessage } from './ErrorMessages'
import QRCodeUpload from './QRCodeUpload'

import '../../css/unlockPin/UnlockPinPage.css'

/**
 * UnlockPinPage component.
 *
 * This component renders a page that allows users to scan or upload a QR code.
 * It uses various sub-components and hooks to manage state and handle API calls.
 *
 * @returns {JSX.Element} The rendered UnlockPinPage component.
 */
export default function UnlockPinPage(): JSX.Element {
  const isAuthorized = useSelector(
    (state: IRootState) => state.identity != null,
  )
  const loading = useSelector(
    (state: IRootState) => state.apiCallsInProgress > 0,
  )

  const { t } = useTranslation()
  // Define the error message types
  const initialErrorMessage: ErrorMessage = { heading: '', content: '' }
  const qrCodeNotFoundErrorMessage: ErrorMessage = {
    heading: t('unlock_pin_page.invaild_qr'),
    content: t('unlock_pin_page.contact_admin'),
  }

  const disableScanning = loading || !isAuthorized

  const [unlockPin, setUnlockPin] = useState<string>('')
  const [isSlideInOpen, setIsSlideInOpen] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] =
    useState<ErrorMessage>(initialErrorMessage)
  const [isError, setIsError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUnlockPinSet, setIsUnlockPinSet] = useState<boolean>(false)

  useEffect(() => {
    if (isSlideInOpen) {
      setIsError(false)
      setErrorMessage(initialErrorMessage)
    }
  }, [isSlideInOpen])

  const handleSubmit = (data: string) => {
    setIsSlideInOpen(true)
    setIsLoading(true)
    if (data === '') {
      setTimeout(() => {
        setIsLoading(false)
        setIsError(true)
        setErrorMessage(qrCodeNotFoundErrorMessage)
      }, 250)
    } else {
      getUnlockPinAsync({ Payload: data })
        .then((value) => {
          setIsLoading(false)
          if (value !== undefined && value !== null) {
            setIsUnlockPinSet(true)
            setUnlockPin(value.toString())
          } else {
            setIsLoading(false)
            setIsError(true)
            setErrorMessage(
              getErrorMessage(
                new AppError({
                  message: t('unlock_pin_page.error_message'),
                  errorCode: 100,
                  name: 'AppError',
                  innerError: null,
                }),
                t,
              ),
            )
          }
        })
        .catch((error) => {
          setIsLoading(false)
          setIsError(true)
          setErrorMessage(getErrorMessage(error, t))
        })
    }
  }

  const closeSlideIn = () => {
    setIsError(false)
    setErrorMessage(initialErrorMessage)
    setIsSlideInOpen(false)
    setUnlockPin('')
    setIsUnlockPinSet(false)
    setIsLoading(false)
  }

  return (
    <div className="container">
      <h3 className="page-header">{t('unlock_pin_page.unlock_pin')}</h3>
      <div className="page-content">
        <Typography variant="subtitle1">
          <div className="responsive-font">
            {isScreenSizeSmall()
              ? t('unlock_pin_page.mobile_instructions')
              : t('unlock_pin_page.desktop_instructions')}
          </div>
        </Typography>
        <QRCodeUpload disable={disableScanning} handleSubmit={handleSubmit} />
      </div>
      <SlideInPage
        title={t('unlock_pin_page.your_pin')}
        isOpen={isSlideInOpen}
        onClose={closeSlideIn}
        isError={isError}
        errorMessage={errorMessage}
      >
        {isLoading && <Spinner />}
        {isUnlockPinSet && (
          <div className="unlock-pin-content-container">
            <h5 className="unlock-pin-content-heading">
              {t('unlock_pin_page.unlock_pin')}
            </h5>
            <div className="unlock-pin-content-border">{unlockPin}</div>
          </div>
        )}
      </SlideInPage>
    </div>
  )
}
